import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import gfm from "remark-gfm";
import { Skeleton, Tooltip } from "src/assets/imports/antdComponents";
import { useTranslation } from "react-i18next";
import { IAudioVideoNarration } from "../../models/interfaces";

// Recursively walks markdown-rendered children, wrapping case-insensitive matches of `query`
// in <mark>. Preserves nested element identity (bold/italic/links/headings/list items) so
// markdown formatting survives the highlight pass.
function highlightChildren(children: React.ReactNode, query: string): React.ReactNode {
	if (!query) return children;
	const lower = query.toLowerCase();
	const qLen = query.length;

	const walk = (node: React.ReactNode, keyPath: string): React.ReactNode => {
		if (typeof node === "string") {
			const text = node;
			const textLower = text.toLowerCase();
			const parts: React.ReactNode[] = [];
			let cursor = 0;
			let i = textLower.indexOf(lower, cursor);
			let markIdx = 0;
			while (i !== -1) {
				if (i > cursor) parts.push(text.slice(cursor, i));
				parts.push(
					<mark key={`${keyPath}-m${markIdx++}`} className="av-search-highlight">
						{text.slice(i, i + qLen)}
					</mark>,
				);
				cursor = i + qLen;
				i = textLower.indexOf(lower, cursor);
			}
			if (parts.length === 0) return text;
			if (cursor < text.length) parts.push(text.slice(cursor));
			return parts;
		}
		if (Array.isArray(node)) {
			return node.map((child, idx) => walk(child, `${keyPath}-${idx}`));
		}
		if (React.isValidElement(node)) {
			const props: any = node.props;
			if (props?.children == null) return node;
			const newChildren = walk(props.children, `${keyPath}-c`);
			return React.cloneElement(node, { key: node.key ?? keyPath }, newChildren);
		}
		return node;
	};

	return walk(children, "h");
}

interface INarrationBlockProps {
	narration: Pick<IAudioVideoNarration, "narration_id" | "content" | "img_url">;
	searchQuery?: string;
}

const NarrationBlock: React.FC<INarrationBlockProps> = ({ narration, searchQuery = "" }) => {
	const { t } = useTranslation();
	const [imgLoaded, setImgLoaded] = useState(false);
	const [imgFailed, setImgFailed] = useState(false);
	const [copied, setCopied] = useState(false);
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);

	// Copies rendered narration as rich text (HTML) + plaintext fallback so paste targets like
	// Word/Docs preserve markdown formatting. Mirrors the chat-response copy in AskKuralHomePage.
	const handleCopy = useCallback(async () => {
		const sourceEl = contentRef.current;
		if (!sourceEl) return;

		const clone = sourceEl.cloneNode(true) as HTMLElement;
		// Unwrap the search-highlight <mark>s so copied output reflects the text, not the UI overlay.
		clone.querySelectorAll("mark.av-search-highlight").forEach((mark) => {
			const parent = mark.parentNode;
			if (!parent) return;
			while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
			parent.removeChild(mark);
		});

		const htmlContent = clone.innerHTML;
		const plainText = (clone.textContent || "")
			.replace(/\n{3,}/g, "\n\n")
			.replace(/^[\t ]+|[\t ]+$/gm, "")
			.trim();

		const markCopied = () => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		};

		if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
			try {
				await navigator.clipboard.write([
					new ClipboardItem({
						"text/html": new Blob([htmlContent], { type: "text/html" }),
						"text/plain": new Blob([plainText], { type: "text/plain" }),
					}),
				]);
				markCopied();
				return;
			} catch (err) {
				console.error("Rich copy failed, falling back to plain text:", err);
			}
		}

		try {
			await navigator.clipboard.writeText(plainText || narration.content || "");
			markCopied();
		} catch (err) {
			console.error("Failed to copy narration:", err);
		}
	}, [narration.content]);

	// Components factory is memoised on searchQuery so ReactMarkdown sees stable component
	// identity when the query doesn't change. Each text-bearing element runs its children
	// through `highlightChildren` to wrap matches.
	const narrationMarkdownComponents = useMemo<Record<string, React.FC<any>>>(() => {
		const hl = (children: React.ReactNode) => highlightChildren(children, searchQuery);
		return {
			code: ({ inline, className, children, ...props }: any) => {
				if (inline) {
					return (
						<code {...props} className="inline-code">
							{hl(children)}
						</code>
					);
				}
				return (
					<code className={className} {...props}>
						{hl(children)}
					</code>
				);
			},
			table: ({ children, ...props }) => (
				<div className="table-container">
					<table className="markdown-table" {...props}>
						{children}
					</table>
				</div>
			),
			thead: ({ children, ...props }) => <thead {...props}>{children}</thead>,
			tbody: ({ children, ...props }) => <tbody {...props}>{children}</tbody>,
			tr: ({ children, ...props }) => <tr {...props}>{children}</tr>,
			th: ({ children, ...props }) => <th {...props}>{hl(children)}</th>,
			td: ({ children, ...props }) => <td {...props}>{hl(children)}</td>,
			p: ({ children, ...props }) => <p {...props}>{hl(children)}</p>,
			li: ({ children, ...props }) => <li {...props}>{hl(children)}</li>,
			hr: ({ children, ...props }) => <hr style={{ margin: "0.5rem 0 0.375rem" }} {...props} />,
			h1: ({ children, ...props }) => (
				<h1 style={{ fontSize: "1.5rem", fontWeight: 600, margin: "0.625rem 0 0.375rem" }} {...props}>
					{hl(children)}
				</h1>
			),
			h2: ({ children, ...props }) => (
				<h2 style={{ fontSize: "1.3333333rem", fontWeight: 600, margin: "0.625rem 0 0.375rem" }} {...props}>
					{hl(children)}
				</h2>
			),
			h3: ({ children, ...props }) => (
				<h3 style={{ fontSize: "1.16666667rem", fontWeight: 600, margin: "0.625rem 0 0.375rem" }} {...props}>
					{hl(children)}
				</h3>
			),
			h4: ({ children, ...props }) => (
				<h4 style={{ fontSize: "1.04166667rem", fontWeight: 600, margin: "0.625rem 0 0.375rem" }} {...props}>
					{hl(children)}
				</h4>
			),
			ul: ({ children, ...props }) => (
				<ul style={{ listStyleType: "disc", paddingLeft: "20px", marginBottom: "2px" }} {...props}>
					{children}
				</ul>
			),
			ol: ({ children, ...props }) => (
				<ol style={{ listStyleType: "decimal", paddingLeft: "20px", marginBottom: "2px" }} {...props}>
					{children}
				</ol>
			),
		};
	}, [searchQuery]);

	// Preload via Image() so the browser fetches even before the <img> mounts. Gated behind
	// an IntersectionObserver so a 50-narration transcript doesn't saturate the connection
	// pool with 50 parallel fetches on initial mount (review B4).
	useEffect(() => {
		if (!narration.img_url || !wrapperRef.current) return;
		setImgLoaded(false);
		setImgFailed(false);
		let cancelled = false;
		const url = narration.img_url;
		const io = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) return;
				io.disconnect();
				const img = new window.Image();
				img.onload = () => {
					if (!cancelled) setImgLoaded(true);
				};
				img.onerror = () => {
					if (!cancelled) setImgFailed(true);
				};
				img.src = url;
			},
			{ rootMargin: "200px" },
		);
		io.observe(wrapperRef.current);
		return () => {
			cancelled = true;
			io.disconnect();
		};
	}, [narration.img_url]);

	return (
		<div ref={wrapperRef} className="av-view-narration" data-narration-id={narration.narration_id}>
			<div className="av-view-narration-header">
				<div className="av-view-narration-header-left">
					<i className="material-symbols-outlined av-view-narration-icon">auto_awesome</i>
					<span className="av-view-narration-title">Narrative Description</span>
				</div>
				{narration.content && (
					<Tooltip placement="left" title={copied ? t("Copied to clipboard") : t("Copy")}>
						<i
							onClick={handleCopy}
							role="button"
							tabIndex={0}
							aria-label={t("Copy narration")}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									handleCopy();
								}
							}}
							className="material-symbols-outlined av-view-narration-copy-btn">
							{copied ? "check" : "copy_all"}
						</i>
					</Tooltip>
				)}
			</div>
			{narration.content && (
				<div ref={contentRef} className="av-view-narration-content">
					<ReactMarkdown components={narrationMarkdownComponents} rehypePlugins={[rehypeRaw]} remarkPlugins={[gfm]}>
						{narration.content}
					</ReactMarkdown>
				</div>
			)}
			{narration.img_url && !imgFailed && (
				<div className="av-view-narration-image-wrapper">
					{imgLoaded ? (
						<img src={narration.img_url} alt="" className="av-view-narration-image" />
					) : (
						<Skeleton.Image active className="av-view-narration-image-skeleton" />
					)}
				</div>
			)}
		</div>
	);
};

export default NarrationBlock;
